import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, Legend, LineChart, Line, Area, AreaChart
} from "recharts";
import { Loader2, Award, MapPin, Briefcase, Clock, Activity, TrendingUp, FileText, UserPlus } from "lucide-react";

const COLORS = ["#2D6BFF", "#1F9D6A", "#B7791F", "#D64545", "#5b21b6", "#0e7490", "#475569", "#0891b2"];

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [adv, setAdv] = useState(null);

  useEffect(() => {
    api.get("/analytics/").then(r => setData(r.data)).catch(() => {});
    api.get("/analytics/advanced").then(r => setAdv(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-slate-400" size={28} /></div>;

  const funnel = [
    { name: "Visitatori", v: data.visitors },
    { name: "Check-up avviati", v: data.assessments_started },
    { name: "Check-up completati", v: data.assessments_completed },
    { name: "Richieste", v: data.requests_received },
    { name: "Shortlist", v: data.shortlists_published },
    { name: "Proposte inviate", v: data.proposals_sent },
    { name: "Progetti attivi", v: data.projects_active },
  ];
  const sectorData = Object.entries(data.sector_distribution || {}).map(([name, value]) => ({ name, value }));
  const roleData = Object.entries(data.role_distribution || {}).map(([name, value]) => ({ name, value })).slice(0, 6);

  return (
    <div>
      <h1 className="font-display text-2xl font-bold text-slate-900 mb-1">Analytics & Reporting</h1>
      <p className="text-sm text-slate-600 mb-6">Funnel di conversione, distribuzioni, performance manager e KPI operativi.</p>

      <Tabs defaultValue="overview" className="w-full" data-testid="analytics-tabs">
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="tab-overview"><Activity size={14} className="mr-1.5" /> Overview</TabsTrigger>
          <TabsTrigger value="advanced" data-testid="tab-advanced"><TrendingUp size={14} className="mr-1.5" /> Performance & KPI</TabsTrigger>
          <TabsTrigger value="managers" data-testid="tab-managers"><Award size={14} className="mr-1.5" /> Top manager</TabsTrigger>
          <TabsTrigger value="operations" data-testid="tab-operations"><Clock size={14} className="mr-1.5" /> Operations</TabsTrigger>
        </TabsList>

        {/* === OVERVIEW === */}
        <TabsContent value="overview" className="mt-5 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label="Tasso conversione" value={`${data.conversion_rate}%`} />
            <StatCard label="Ricavi totali" value={`€${(data.revenue_total || 0).toLocaleString("it-IT")}`} />
            <StatCard label="Valore medio progetto" value={`€${(data.avg_project_value || 0).toLocaleString("it-IT")}`} />
            <StatCard label="Rating medio" value={data.avg_rating || "—"} />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="cxo-card p-5">
              <h3 className="font-semibold text-slate-900">Funnel di conversione</h3>
              <div className="mt-4 h-72">
                <ResponsiveContainer>
                  <BarChart data={funnel} layout="vertical" margin={{ left: 80 }}>
                    <CartesianGrid stroke="rgba(100,116,139,0.2)" strokeDasharray="3 3" />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={130} />
                    <Tooltip />
                    <Bar dataKey="v" fill="#2D6BFF" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="cxo-card p-5">
              <h3 className="font-semibold text-slate-900">Trend mensile</h3>
              <div className="mt-4 h-72">
                <ResponsiveContainer>
                  <LineChart data={data.monthly_trend}>
                    <CartesianGrid stroke="rgba(100,116,139,0.2)" strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="requests" stroke="#2D6BFF" strokeWidth={2} />
                    <Line type="monotone" dataKey="projects" stroke="#1F9D6A" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="cxo-card p-5">
              <h3 className="font-semibold text-slate-900">Distribuzione per settore</h3>
              <div className="mt-4 h-72">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={sectorData} dataKey="value" nameKey="name" outerRadius={80} label>
                      {sectorData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="cxo-card p-5">
              <h3 className="font-semibold text-slate-900">Ruoli più richiesti</h3>
              <div className="mt-4 h-72">
                <ResponsiveContainer>
                  <BarChart data={roleData} layout="vertical" margin={{ left: 140 }}>
                    <CartesianGrid stroke="rgba(100,116,139,0.2)" strokeDasharray="3 3" />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={150} />
                    <Tooltip />
                    <Bar dataKey="value" fill="#1F9D6A" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* === ADVANCED PERFORMANCE === */}
        <TabsContent value="advanced" className="mt-5 space-y-4">
          {!adv ? (
            <div className="flex items-center justify-center py-10"><Loader2 className="animate-spin text-slate-400" /></div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard label="Tempo medio a shortlist" value={`${adv.avg_time_to_shortlist_days} gg`} icon={Clock} />
                <StatCard label="Tempo medio a progetto" value={`${adv.avg_time_to_project_days} gg`} icon={Briefcase} />
                <StatCard label="Utilizzo medio manager" value={`${adv.avg_manager_utilization_pct}%`} icon={Activity} />
                <StatCard label="Documenti caricati" value={adv.documents_total} icon={FileText} sub={`+${adv.documents_recent_30d} ultimi 30gg`} />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="cxo-card p-5">
                  <h3 className="font-semibold text-slate-900">Stato dei progetti</h3>
                  <div className="mt-4 h-64">
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie
                          data={Object.entries(adv.project_status || {}).map(([name, value]) => ({ name, value }))}
                          dataKey="value" nameKey="name" outerRadius={90} label
                        >
                          {Object.keys(adv.project_status || {}).map((_, i) => (
                            <Cell key={i} fill={COLORS[i % COLORS.length]} />
                          ))}
                        </Pie>
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="cxo-card p-5">
                  <h3 className="font-semibold text-slate-900">Salute dei progetti</h3>
                  <p className="text-xs text-slate-500 mt-1">Progetti attivi categorizzati per stato di rischio</p>
                  <div className="mt-4 grid grid-cols-3 gap-3 text-center">
                    <HealthCard label="On-track" value={adv.project_health?.on_track || 0} color="#1F9D6A" />
                    <HealthCard label="A rischio" value={adv.project_health?.at_risk || 0} color="#D64545" />
                    <HealthCard label="Completati" value={adv.project_health?.completed || 0} color="#475569" />
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-3 text-sm text-slate-700">
                    <div className="p-3 rounded-lg bg-slate-50">
                      <p className="text-xs uppercase tracking-wider text-slate-500">Shortlist entro 3gg</p>
                      <p className="font-display text-2xl font-bold cxo-tabular">{adv.shortlists_under_3_days}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-slate-50">
                      <p className="text-xs uppercase tracking-wider text-slate-500">Referral approvati</p>
                      <p className="font-display text-2xl font-bold cxo-tabular">{adv.referrals_approved}/{adv.referrals_total}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="cxo-card p-5">
                <h3 className="font-semibold text-slate-900">Settore × Ruolo (top combinazioni)</h3>
                <p className="text-xs text-slate-500 mt-1">Quali competenze sono più richieste in ciascun settore</p>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {(adv.sector_role_heatmap || []).slice(0, 12).map((h, i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                      <div>
                        <p className="text-xs text-slate-500">{h.sector}</p>
                        <p className="font-semibold text-slate-900 text-sm">{h.role}</p>
                      </div>
                      <Badge className="bg-[#EAF0FF] text-[#2D6BFF] border-0">{h.count}</Badge>
                    </div>
                  ))}
                  {!adv.sector_role_heatmap?.length && <p className="text-sm text-slate-500 col-span-3">Nessun dato.</p>}
                </div>
              </div>
            </>
          )}
        </TabsContent>

        {/* === TOP MANAGERS === */}
        <TabsContent value="managers" className="mt-5 space-y-4">
          {!adv ? (
            <div className="flex items-center justify-center py-10"><Loader2 className="animate-spin text-slate-400" /></div>
          ) : (
            <>
              <div className="cxo-card overflow-hidden" data-testid="top-managers-table">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="p-3 text-left">#</th>
                      <th className="p-3 text-left">Manager</th>
                      <th className="p-3 text-left">Ruolo</th>
                      <th className="p-3 text-left">Città</th>
                      <th className="p-3 text-left">Esperienza</th>
                      <th className="p-3 text-left">Rating</th>
                      <th className="p-3 text-left">Progetti</th>
                      <th className="p-3 text-left">Ricavi gen.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(adv.top_managers || []).map((m, i) => (
                      <tr key={m.id} className="border-t border-slate-100 hover:bg-slate-50">
                        <td className="p-3 cxo-tabular text-slate-500">{i + 1}</td>
                        <td className="p-3 font-medium text-slate-900">{m.name}</td>
                        <td className="p-3 text-slate-600">{m.role}</td>
                        <td className="p-3 text-slate-600 inline-flex items-center gap-1"><MapPin size={12} className="text-slate-400" />{m.city}</td>
                        <td className="p-3 cxo-tabular">{m.years}+ anni</td>
                        <td className="p-3 cxo-tabular font-semibold text-[#B7791F]">{m.rating || "—"}</td>
                        <td className="p-3 cxo-tabular">{m.projects}</td>
                        <td className="p-3 cxo-tabular text-[#1F9D6A] font-medium">€{(m.revenue || 0).toLocaleString("it-IT")}</td>
                      </tr>
                    ))}
                    {!adv.top_managers?.length && <tr><td colSpan={8} className="p-10 text-center text-slate-500">Nessun manager attivo.</td></tr>}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </TabsContent>

        {/* === OPERATIONS === */}
        <TabsContent value="operations" className="mt-5 space-y-4">
          {!adv ? (
            <div className="flex items-center justify-center py-10"><Loader2 className="animate-spin text-slate-400" /></div>
          ) : (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="cxo-card p-5">
                  <h3 className="font-semibold text-slate-900 flex items-center gap-2"><MapPin size={16} /> Distribuzione manager per area</h3>
                  <div className="mt-4 h-72">
                    <ResponsiveContainer>
                      <BarChart
                        data={Object.entries(adv.manager_geo_distribution || {}).map(([name, value]) => ({ name, value }))}
                        layout="vertical" margin={{ left: 100 }}
                      >
                        <CartesianGrid stroke="rgba(100,116,139,0.2)" strokeDasharray="3 3" />
                        <XAxis type="number" tick={{ fontSize: 11 }} />
                        <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={120} />
                        <Tooltip />
                        <Bar dataKey="value" fill="#5b21b6" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="cxo-card p-5">
                  <h3 className="font-semibold text-slate-900 flex items-center gap-2"><UserPlus size={16} /> Referral pipeline</h3>
                  <div className="mt-4 h-72">
                    <ResponsiveContainer>
                      <BarChart
                        data={Object.entries(adv.referrals_by_status || {}).map(([name, value]) => ({ name, value }))}
                      >
                        <CartesianGrid stroke="rgba(100,116,139,0.2)" strokeDasharray="3 3" />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Bar dataKey="value" fill="#B7791F" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              <div className="cxo-card p-5">
                <h3 className="font-semibold text-slate-900">KPI temporali</h3>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 rounded-xl bg-gradient-to-br from-[#EAF0FF] to-white border border-[#2D6BFF]/15">
                    <p className="text-xs uppercase tracking-wider text-[#2D6BFF] font-semibold">Tempo medio → Shortlist</p>
                    <p className="font-display text-3xl font-bold cxo-tabular mt-2 text-slate-900">{adv.avg_time_to_shortlist_days}<span className="text-base text-slate-500"> gg</span></p>
                  </div>
                  <div className="p-4 rounded-xl bg-gradient-to-br from-[#E9F8F1] to-white border border-[#1F9D6A]/15">
                    <p className="text-xs uppercase tracking-wider text-[#1F9D6A] font-semibold">Tempo medio → Progetto</p>
                    <p className="font-display text-3xl font-bold cxo-tabular mt-2 text-slate-900">{adv.avg_time_to_project_days}<span className="text-base text-slate-500"> gg</span></p>
                  </div>
                  <div className="p-4 rounded-xl bg-gradient-to-br from-[#FFF6E6] to-white border border-[#B7791F]/15">
                    <p className="text-xs uppercase tracking-wider text-[#B7791F] font-semibold">Utilizzo manager medio</p>
                    <p className="font-display text-3xl font-bold cxo-tabular mt-2 text-slate-900">{adv.avg_manager_utilization_pct}<span className="text-base text-slate-500"> %</span></p>
                  </div>
                </div>
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

const StatCard = ({ label, value, icon: Icon, sub }) => (
  <div className="cxo-card p-4">
    <div className="flex items-center gap-2 text-slate-500">
      {Icon && <Icon size={14} />}
      <p className="text-xs uppercase tracking-wider font-semibold">{label}</p>
    </div>
    <p className="mt-2 text-2xl font-display font-bold cxo-tabular text-slate-900">{value}</p>
    {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
  </div>
);

const HealthCard = ({ label, value, color }) => (
  <div className="rounded-lg border border-slate-200 p-4">
    <p className="text-xs uppercase tracking-wider text-slate-500">{label}</p>
    <p className="font-display text-3xl font-bold cxo-tabular mt-1" style={{ color }}>{value}</p>
  </div>
);
